import { useEffect, useState, useRef } from 'react';
import { NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLoginDrawer } from '../../context/LoginDrawerContext';
import { useLanguage, LANGUAGES, getLanguageLabel, getLanguageShortLabel } from '../../context/LanguageContext';
import { t } from '../../i18n/translations';
import logo from '../../assets/images/daiv_logo.png';
import poojaIcon from '../../assets/images/pooja.png';
import astroTabIcon from '../../assets/images/astro.png';
import blogTabIcon from '../../assets/images/blog.png';
import storeTabIcon from '../../assets/svg/store.svg';
import icon1 from '../../assets/images/icon-1.png';
import flag from '../../assets/images/flag.webp';
import nowOnMobile from '../../assets/images/now_on_mobile.png';
import { resolveImageUrl } from '../../config/api';
import { getMainNavItems, getDesktopTopNavItems, MOBILE_BOTTOM_PRIMARY_IDS } from './navItems';
import { useFeatureFlags } from '../../context/FeatureFlagsContext';
import { FEATURE_KEYS } from '../../utils/featureFlags';
import { prefetchRouteChunk } from '../../utils/splashChrome';
import StoreNavCartButton from '../StoreNavCartButton/StoreNavCartButton';
import './Navbar.css';

const warmRouteChunk = (to) => () => {
  if (typeof to === 'string') prefetchRouteChunk(to);
};

function getProfileInitial(name) {
  const trimmed = String(name || 'User').trim();
  return (trimmed.charAt(0) || 'U').toUpperCase();
}

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  /** 0–1: top progress line (scaleX) — scrollTop / (scrollHeight − viewport) */
  const progressRef = useRef(null);
  const mobileProgressRef = useRef(null);
  const [langOpen, setLangOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const langMenuRef = useRef(null);
  const mobileLangRef = useRef(null);
  const mobileHeaderRef = useRef(null);
  const { lang, setLang } = useLanguage();

  const { user, isAuthenticated } = useAuth();
  const { open: openLoginDrawer } = useLoginDrawer();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location?.pathname || '';
  const { flags } = useFeatureFlags();
  const navFlags = {
    storeEnabled: Boolean(flags[FEATURE_KEYS.WEB_STORE]),
    liveTvEnabled: Boolean(flags[FEATURE_KEYS.LIVE_TV]),
  };
  const mainNavItems = getMainNavItems(pathname, navFlags);
  const desktopTopNavItems = getDesktopTopNavItems(pathname, navFlags);
  const isGalleryPage = pathname === '/gallery';
  const debugNav = process.env.NODE_ENV !== 'production' && process.env.REACT_APP_DEBUG_NAV === 'true';

  const closeDrawer = () => setDrawerOpen(false);

  const logNavClick = (item, source) => {
    if (!debugNav) return;
    console.info('[Navbar]', source, 'click', {
      id: item?.id,
      to: item?.to,
      currentPath: pathname,
    });
  };

  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!debugNav) return;
    console.info('[Navbar] route changed', { pathname });
  }, [debugNav, pathname]);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeDrawer();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [drawerOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const progress = docHeight > 0 ? scrollTop / docHeight : 0;
      if (progressRef.current) {
        progressRef.current.style.width = `${progress * 100}%`;
      }
      if (mobileProgressRef.current) {
        mobileProgressRef.current.style.width = `${progress * 100}%`;
      }
    };

    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [pathname]);

  useEffect(() => {
    function handleClickOutside(e) {
      const inLangMenu = langMenuRef.current?.contains(e.target) || mobileLangRef.current?.contains(e.target);
      if (langOpen && !inLangMenu) setLangOpen(false);
    }
    if (langOpen) document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [langOpen]);

  const userLabel = user?.name || user?.username || 'User';
  const userInitial = getProfileInitial(userLabel);
  const profileImageUrl = user?.profileImageUrl ? resolveImageUrl(user.profileImageUrl) : null;

  const handleUserClick = () => {
    if (isAuthenticated) {
      navigate('/profile');
    } else {
      openLoginDrawer();
    }
  };

  const mobileBottomIcon = (itemId) => {
    switch (itemId) {
      case 'home':
        return (
          <div className="daiv-mobile-icon-wrapper daiv-mobile-icon-home-redtext">
            <img src={logo} alt="" aria-hidden="true" width="48" height="48" decoding="async" />
          </div>
        );
      case 'pooja':
        return (
          <div className="daiv-mobile-icon-wrapper daiv-mobile-icon-img daiv-mobile-tab-icon-unified daiv-mobile-icon-pooja">
            <img src={poojaIcon} alt="" aria-hidden="true" width="32" height="32" decoding="async" />
          </div>
        );
      case 'temple':
        return (
          <div className="daiv-mobile-icon-wrapper daiv-mobile-icon-img daiv-mobile-icon-kshetra">
            <img src={icon1} alt="" aria-hidden="true" width={24} height={24} />
          </div>
        );
      case 'mobileApp':
        return (
          <div className="daiv-mobile-icon-wrapper daiv-mobile-icon-img daiv-mobile-icon-mobile">
            <img src={nowOnMobile} alt="" aria-hidden="true" width={24} height={24} />
          </div>
        );
      case 'astrology':
        return (
          <div className="daiv-mobile-icon-wrapper daiv-mobile-icon-img daiv-mobile-tab-icon-unified daiv-mobile-icon-astro">
            <img src={astroTabIcon} alt="" aria-hidden="true" width="32" height="32" decoding="async" />
          </div>
        );
      case 'blogs':
        return (
          <div className="daiv-mobile-icon-wrapper daiv-mobile-icon-img daiv-mobile-tab-icon-unified daiv-mobile-icon-blog">
            <img src={blogTabIcon} alt="" aria-hidden="true" width="32" height="32" decoding="async" />
          </div>
        );
      case 'store':
        return (
          <div className="daiv-mobile-icon-wrapper daiv-mobile-icon-img daiv-mobile-tab-icon-unified daiv-mobile-icon-store">
            <img src={storeTabIcon} alt="" aria-hidden="true" width="32" height="32" decoding="async" />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <header className={`daiv-header ${isScrolled ? 'daiv-header-scrolled' : ''}`} id="home">
        {/* Fixed to navbar edge — one bar for desktop + mobile */}
        <div
          ref={progressRef}
          className="daiv-header-progress"
          style={{ width: '0%' }}
          aria-hidden="true"
        />
        <div className="daiv-container">
          <div className="daiv-header-inner">
            <div className="daiv-header-left">
              <Link to="/" className="daiv-logo-group" aria-label="Go to home">
                <img src={logo} alt="Daiv" className="daiv-logo" width="45" height="48" />
                <div className="daiv-header-brand">
                  <span className="daiv-brand-name">{t('nav.daiv', lang)}</span>
                  <span className="daiv-tagline">धर्मो रक्षति रक्षितः</span>
                </div>
              </Link>
            </div>
            <div className="daiv-header-right">
              <nav className="daiv-nav">
                {desktopTopNavItems.map((item) =>
                  item.linkKind === 'navlink' ? (
                    <NavLink
                      key={item.id}
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) => (isActive ? 'daiv-nav-link active' : 'daiv-nav-link')}
                      onMouseEnter={warmRouteChunk(item.to)}
                      onFocus={warmRouteChunk(item.to)}
                      onClick={() => logNavClick(item, 'desktop')}
                    >
                      {({ isActive }) => (
                        <>
                          {t(item.labelKey, lang)}
                          {isActive && (
                            <img src={flag} alt="" className="daiv-nav-tab-flag" aria-hidden="true" width={26} height={26} loading="lazy" decoding="async" />
                          )}
                        </>
                      )}
                    </NavLink>
                  ) : (
                    <Link
                      key={item.id}
                      to={item.to}
                      className={`daiv-nav-link ${item.active ? 'active' : ''}`}
                      onMouseEnter={warmRouteChunk(item.to)}
                      onFocus={warmRouteChunk(item.to)}
                      onClick={() => logNavClick(item, 'desktop')}
                    >
                      {t(item.labelKey, lang)}
                      {item.active && (
                        <img src={flag} alt="" className="daiv-nav-tab-flag" aria-hidden="true" width={26} height={26} loading="lazy" decoding="async" />
                      )}
                    </Link>
                  ),
                )}
              </nav>
              <div className="daiv-header-actions">
                <div className="daiv-lang-menu-wrap" ref={langMenuRef}>
                  <button
                    type="button"
                    className="daiv-lang-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setLangOpen((o) => !o);
                    }}
                    aria-haspopup="listbox"
                    aria-expanded={langOpen}
                    aria-label={`${getLanguageShortLabel(lang)} — ${t('nav.selectLanguage', lang)}: ${getLanguageLabel(lang)}`}
                  >
                    {getLanguageShortLabel(lang)} ▾
                  </button>
                  {langOpen && (
                    <ul className="daiv-lang-dropdown" role="listbox" aria-label={t('nav.selectLanguage', lang)}>
                      {LANGUAGES.map((l) => (
                        <li key={l.code} role="option" aria-selected={lang === l.code}>
                          <button
                            type="button"
                            className={lang === l.code ? 'daiv-lang-option active' : 'daiv-lang-option'}
                            onClick={() => {
                              setLang(l.code);
                              setLangOpen(false);
                            }}
                          >
                            {l.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <Link to="/poojas" className="daiv-nav-sign-in-btn daiv-nav-book-pooja-link" aria-label={t('nav.bookAPooja', lang)}>
                  {t('nav.bookAPooja', lang)}
                </Link>
                <div className="daiv-user-menu-wrap">
                  {isAuthenticated ? (
                    <button
                      type="button"
                      className="daiv-nav-avatar daiv-nav-avatar-button logged-in"
                      title={userLabel}
                      aria-label={userLabel}
                      aria-haspopup="dialog"
                      onClick={handleUserClick}
                    >
                      {profileImageUrl ? (
                        <img src={profileImageUrl} alt="" className="daiv-nav-avatar-img" />
                      ) : (
                        <span className="daiv-nav-avatar-text">{userInitial}</span>
                      )}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="daiv-book-pooja-btn"
                      onClick={handleUserClick}
                      aria-label={t('nav.signIn', lang)}
                    >
                      {t('nav.signIn', lang)}
                    </button>
                  )}
                </div>
                {navFlags.storeEnabled ? <StoreNavCartButton /> : null}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile top bar - search, language, login - only on mobile */}
      <header className="daiv-mobile-header" ref={mobileHeaderRef}>
        <div className="daiv-mobile-header-inner">
          <Link to="/" className="daiv-mobile-header-brand" aria-label={`${t('nav.daiv', lang)} धर्मो रक्षति रक्षितः — Go to home`}>
            <img src={logo} alt="Daiv" className="daiv-mobile-header-logo" width="42" height="42" />
            <div className="daiv-mobile-header-brand-text">
              <span className="daiv-mobile-header-brand-name">{t('nav.daiv', lang)}</span>
              <span className="daiv-mobile-header-tagline">धर्मो रक्षति रक्षितः</span>
            </div>
          </Link>
          <div className="daiv-mobile-header-actions">
            <div className="daiv-mobile-header-lang" ref={mobileLangRef}>
              <button
                type="button"
                className="daiv-mobile-header-lang-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setLangOpen((o) => !o);
                }}
                aria-haspopup="listbox"
                aria-expanded={langOpen}
                aria-label={`${getLanguageShortLabel(lang)} — ${t('nav.selectLanguage', lang)}: ${getLanguageLabel(lang)}`}
              >
                {getLanguageShortLabel(lang)}
              </button>
              {langOpen && (
                <ul className="daiv-mobile-header-lang-dropdown" role="listbox" aria-label={t('nav.selectLanguage', lang)}>
                  {LANGUAGES.map((l) => (
                    <li key={l.code} role="option" aria-selected={lang === l.code}>
                      <button
                        type="button"
                        className={lang === l.code ? 'daiv-lang-option active' : 'daiv-lang-option'}
                        onClick={() => {
                          setLang(l.code);
                          setLangOpen(false);
                        }}
                      >
                        {l.label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {navFlags.storeEnabled ? (
              <StoreNavCartButton className="daiv-mobile-header-cart-btn" />
            ) : null}
            <button
              type="button"
              className={`daiv-mobile-header-menu-btn${drawerOpen ? ' daiv-mobile-header-menu-btn--open' : ''}`}
              onClick={() => setDrawerOpen((o) => !o)}
              aria-expanded={drawerOpen}
              aria-controls="daiv-mobile-drawer"
              aria-label={drawerOpen ? t('nav.closeMenu', lang) : t('nav.openMenu', lang)}
            >
              {drawerOpen ? (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              ) : (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <path d="M4 7h16M4 12h16M4 17h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </header>

      {drawerOpen && (
        <>
          <div
            className="daiv-mobile-drawer-backdrop"
            onClick={closeDrawer}
            onKeyDown={(e) => e.key === 'Enter' && closeDrawer()}
            role="presentation"
            aria-hidden="true"
          />
          <div
            id="daiv-mobile-drawer"
            className="daiv-mobile-drawer-panel"
            role="dialog"
            aria-modal="true"
            aria-label={t('nav.mobileDrawerAria', lang)}
          >
            <div className="daiv-mobile-drawer-head">
              <span className="daiv-mobile-drawer-title">{t('nav.mobileDrawerTitle', lang)}</span>
              <button type="button" className="daiv-mobile-drawer-close" onClick={closeDrawer} aria-label={t('nav.closeMenu', lang)}>
                ×
              </button>
            </div>
            <nav className="daiv-mobile-drawer-nav" aria-label={t('nav.mobileDrawerAria', lang)}>
              <ul className="daiv-mobile-drawer-list">
                {mainNavItems.map((item) => (
                  <li key={item.id}>
                    {item.linkKind === 'navlink' ? (
                      <NavLink
                        to={item.to}
                        end={item.end}
                        className={({ isActive }) => `daiv-mobile-drawer-link${isActive ? ' active' : ''}`}
                        onMouseEnter={warmRouteChunk(item.to)}
                        onFocus={warmRouteChunk(item.to)}
                        onClick={() => {
                          logNavClick(item, 'mobile-drawer');
                          closeDrawer();
                        }}
                      >
                        {t(item.labelKey, lang)}
                      </NavLink>
                    ) : (
                      <Link
                        to={item.to}
                        className={`daiv-mobile-drawer-link${item.active ? ' active' : ''}`}
                        onMouseEnter={warmRouteChunk(item.to)}
                        onFocus={warmRouteChunk(item.to)}
                        onClick={() => {
                          logNavClick(item, 'mobile-drawer');
                          closeDrawer();
                        }}
                      >
                        {t(item.labelKey, lang)}
                      </Link>
                    )}
                  </li>
                ))}
                <li>
                  <Link to="/gallery" className={`daiv-mobile-drawer-link${isGalleryPage ? ' active' : ''}`} onClick={closeDrawer}>
                    {t('nav.gallery', lang)}
                  </Link>
                </li>
                <li>
                  {isAuthenticated ? (
                    <NavLink
                      to="/profile"
                      className={({ isActive }) => `daiv-mobile-drawer-link${isActive ? ' active' : ''}`}
                      onClick={closeDrawer}
                    >
                      {t('profile.myProfile', lang)}
                    </NavLink>
                  ) : (
                    <button
                      type="button"
                      className="daiv-mobile-drawer-link daiv-mobile-drawer-link-btn"
                      onClick={() => {
                        closeDrawer();
                        handleUserClick();
                      }}
                    >
                      {t('nav.signIn', lang)}
                    </button>
                  )}
                </li>
              </ul>
            </nav>
            <div className="daiv-mobile-drawer-footer">
              <Link to="/poojas" className="daiv-mobile-drawer-cta" onClick={closeDrawer}>
                {t('nav.bookAPooja', lang)}
              </Link>
            </div>
          </div>
        </>
      )}

      <nav className="daiv-mobile-nav" aria-label={t('nav.mobileBottomNavAria', lang)}>
        {/* Bottom progress bar, sits inside the mobile nav overlay at the very bottom */}
        <div
          ref={mobileProgressRef}
          className="daiv-mobile-bottom-progress"
          aria-hidden="true"
        />
        <div className="daiv-mobile-nav-inner">
          {MOBILE_BOTTOM_PRIMARY_IDS.map((navId) => {
            const item = mainNavItems.find((x) => x.id === navId);
            if (!item) return null;
            const label = item.id === 'home' ? t('nav.daiv', lang) : t(item.labelKey, lang);
            const inner = (
              <>
                {mobileBottomIcon(item.id)}
                {item.id !== 'home' && <span className="daiv-mobile-label">{label}</span>}
              </>
            );
            if (item.linkKind === 'navlink') {
              return (
                <NavLink
                  key={item.id}
                  to={item.to}
                  end={item.end}
                  aria-label={item.id === 'home' ? t('nav.home', lang) : undefined}
                  className={({ isActive }) =>
                    (isActive ? 'daiv-mobile-nav-item active' : 'daiv-mobile-nav-item') +
                    (item.id === 'home' ? ' daiv-mobile-nav-item--home-logo' : '')
                  }
                  onClick={() => logNavClick(item, 'mobile-bottom')}
                >
                  {inner}
                </NavLink>
              );
            }
            return (
              <Link
                key={item.id}
                to={item.to}
                className={`daiv-mobile-nav-item ${item.active ? 'active' : ''}`}
                onClick={() => logNavClick(item, 'mobile-bottom')}
              >
                {inner}
              </Link>
            );
          })}

          {isAuthenticated ? (
            <NavLink to="/profile" className={({ isActive }) => (isActive ? 'daiv-mobile-nav-item active' : 'daiv-mobile-nav-item')}>
              <div className="daiv-mobile-icon-wrapper daiv-mobile-profile-avatar">
                {profileImageUrl ? (
                  <img src={profileImageUrl} alt="" aria-hidden="true" />
                ) : (
                  <span className="daiv-mobile-avatar-initial" aria-hidden="true">{userInitial}</span>
                )}
              </div>
              <span className="daiv-mobile-label">{t('nav.profileTab', lang)}</span>
            </NavLink>
          ) : (
            <button
              type="button"
              className="daiv-mobile-nav-item daiv-mobile-nav-item-btn"
              onClick={handleUserClick}
              aria-label={t('nav.signIn', lang)}
            >
              <div className="daiv-mobile-icon-wrapper daiv-mobile-tab-icon-unified daiv-mobile-icon-signin">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle cx="12" cy="7" r="4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <span className="daiv-mobile-label">{t('nav.signIn', lang)}</span>
            </button>
          )}
        </div>
      </nav>
    </>
  );
}
